"use client"
import Link from "next/link"
import { useEffect, useState } from "react"

interface BlogPost {
  id: string
  slug: string
  title: string
  body: string
  category: string
  metaDescription: string
  excerpt: string | null
  coverImage: string | null
  publishedAt: string
}

const catClass: Record<string, string> = {
  "Fraud Alert": "alert",
  "Diaspora Guide": "guide",
  "Market Update": "market",
  "Buying Guide": "guide",
}

const catColors: Record<string, string> = {
  "Fraud Alert": "linear-gradient(135deg,#c44444,#142142)",
  "Diaspora Guide": "linear-gradient(135deg,#3d8b5f,#1B2D54)",
  "Market Update": "linear-gradient(135deg,#C8963E,#a87a2c)",
  "Buying Guide": "linear-gradient(135deg,#2a3f6e,#1B2D54)",
}

export default function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState("All")

  useEffect(() => {
    fetch("/api/blog")
      .then(r => r.json())
      .then(d => { setPosts(d.data ?? []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  const filtered = filter === "All" ? posts : posts.filter(p => p.category === filter)

  return (
    <div style={{ background: "var(--cream)", minHeight: "100vh" }}>
      <div style={{ background: "var(--navy)", padding: "56px 24px 48px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <span className="eyebrow" style={{ background: "rgba(200,150,62,0.15)", borderColor: "rgba(200,150,62,0.3)", color: "var(--gold-soft)" }}>Blog</span>
          <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 48, fontWeight: 700, color: "var(--cream)", margin: "20px 0 12px" }}>
            Property <em style={{ color: "var(--gold)" }}>insights</em>.
          </h1>
          <p style={{ color: "rgba(253,251,247,0.75)", fontSize: 16, maxWidth: 560 }}>Market updates, fraud alerts, buying guides, and diaspora resources.</p>
        </div>
      </div>
      <div className="section" style={{ paddingTop: 40 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 28, flexWrap: "wrap" }}>
          {["All", "Fraud Alert", "Buying Guide", "Market Update", "Diaspora Guide"].map(cat => (
            <button key={cat} onClick={() => setFilter(cat)} style={{ padding: "8px 18px", borderRadius: 20, border: "1px solid var(--line)", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", background: filter === cat ? "var(--navy)" : "var(--cream)", color: filter === cat ? "var(--cream)" : "var(--navy)" }}>{cat}</button>
          ))}
        </div>
        {loading ? (
          <div className="blog-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ background: "var(--cream-warm)", border: "1px solid var(--line)", borderRadius: 16, height: 320, opacity: 0.5 }}></div>
            ))}
          </div>
        ) : (
          <div className="blog-grid">
            {filtered.map(post => (
              <Link key={post.id} href={"/blog/" + post.slug} className="blog-card">
                <div className="blog-card-img" style={post.coverImage ? {
                  backgroundImage: `url(${post.coverImage})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                } : {
                  background: catColors[post.category] ?? "linear-gradient(135deg,#1B2D54,#2a3f6e)",
                }}></div>
                <div className="blog-card-body">
                  <span className={"blog-cat " + (catClass[post.category] ?? "guide")}>{post.category}</span>
                  <h3>{post.title}</h3>
                  <p className="blog-excerpt">{post.excerpt || post.metaDescription}</p>
                  <div className="blog-meta"><span>{new Date(post.publishedAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span></div>
                </div>
              </Link>
            ))}
          </div>
        )}
        {filtered.length === 0 && !loading && (
          <div style={{ textAlign: "center", padding: 64, color: "var(--muted)" }}>No posts in this category yet.</div>
        )}
      </div>
    </div>
  )
}